/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Avatar, Body1, Button, Persona, Text, Tooltip, makeStyles, mergeClasses, tokens, typographyStyles } from "@fluentui/react-components";
import { Column, CustomCardHeader, Row } from "../common";
import { ContactPanel } from "./ContactPanel";
import { SelectContactsDialog } from "../dialogs/SelectContactsDialog";
import { BrandCard } from "../common/BrandCard";
import { ConfirmationDialog } from "../dialogs/ConfirmationDialog";
import { AddCircle12Regular, ContactCardRegular, Dismiss16Regular } from "@fluentui/react-icons";
import { useCommonStyles } from "../common/Styles/commonStyles";
import { Contact, ControlSize, Party, Phone } from "../../data/types";
import { PhoneControl } from "./PhoneControl";
import { appTokens } from "../common/Styles/appTokens";

const useStyles = makeStyles({
    card: {
        backgroundColor: appTokens.partyControl.background
    },
    caption: {
        color: appTokens.text.colors.caption,
    },
    subtextColumn: {
        gap: '0'
    },
    contactsTitle: {
        ...typographyStyles.body1Strong
    },
    addContactBtn: {
        fontSize: '13px',
    }
});

export type PartyControlProps = {
    contacts?: Contact[];
    party?: Party;
    options?: boolean;
    size?: ControlSize;
    selectable?: boolean;
    onSelectionChange?: (selParty: Party, selected: boolean) => void;
    className?: string;
    addContacts?: boolean;
    removeContacts?: boolean;
    onContactsAddOrChange?: (contacts: Contact[]) => void;
    onContactRemoved?: (contact: Contact) => void;
    onRemoveParty?: () => void;
    creating?: boolean;
}
export const PartyControl = (props: PartyControlProps) => {

    const { party, contacts, className, size = 'small', creating = false, selectable = false, onSelectionChange, addContacts = false, removeContacts = false, onContactsAddOrChange, onContactRemoved, onRemoveParty } = props;

    //State
    const [isSelected, setSelected] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const cardClasses = mergeClasses(
        styles.card,
        className
    );
    const addContactBtnClasses = mergeClasses(
        commonStyles.button.root,
        styles.addContactBtn,
    );

    //Logic
    const cityStateZip = () => {
        if (!!party?.address?.city || !!party?.address?.state || !!party?.address?.zip || !!party?.address?.country)
            return `${party?.address?.city || ''}, ${party?.address?.state || ''} ${party?.address?.zip || ''}`;
        else return '';
    }
    const getPhoneText = () => {
        if (!party?.phone?.[0]) return '';
        else {
            return `${!!party?.phone?.[0]?.type ? `${party?.phone?.[0]?.type} :` : ''}${party?.phone?.[0]?.number}`;
        }
    }
    const getPrimaryPhone = () => {
        return party?.phone?.filter((p) => p.primary)?.[0];
    }
    const getFirstNotPrimaryPhone = () => {
        return party?.phone?.filter((p) => !p.primary)?.[0];
    }

    //UI Events
    const onSelectedChange = (_: any, state: any) => {
        setSelected(state.selected || state.checked);
        !!onSelectionChange && onSelectionChange(party as Party, state.selected || state.checked);
    }
    const onContactSubmit = (contacts: Contact[]) => {
        if (!!onContactsAddOrChange) onContactsAddOrChange(contacts);
    }
    const onRemoveContact = (contact: Contact) => {
        if (!!onContactRemoved) onContactRemoved(contact);
    }

    return (
        <>
            {size === 'large' && (
                <BrandCard
                    className={cardClasses}
                    title={`${party?.name || (creating ? '[Name of party]' : '')}`}
                    description={party?.url}
                    headerIcon={
                        <Avatar
                            name={party?.name || (creating ? 'Name of party' : '')}
                            color='colorful'
                            shape='square'
                            image={{ src: party?.logoUrl as string }}
                            size={40}
                        />
                    }
                    action={
                        <>
                            {!!onRemoveParty && (
                                <ConfirmationDialog
                                    title='Delete Party?'
                                    text='Are you sure you want to remove this party?'
                                    onConfirm={onRemoveParty}
                                >
                                    <Button
                                        className={commonStyles.button.root}
                                        appearance='transparent'
                                        icon={<Dismiss16Regular />}
                                        data-testid='party-control-dismiss-button'
                                    />
                                </ConfirmationDialog>
                            )}
                        </>
                    }
                    maxWidth='375px'
                    width='100%'
                    minWidth='300px'
                    backgroundColor={appTokens.partyControl.background}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                >
                    <Row padding='pt-3'>
                        <Column className={styles.subtextColumn} overflowX='clip'>
                            <Body1 className={styles.caption}>
                                {party?.address?.address1 || (creating ? '[Address]' : '')}
                            </Body1>
                            <Body1 className={styles.caption}>{party?.address?.address2}</Body1>
                            <Body1 className={styles.caption}>{cityStateZip()}</Body1>
                        </Column>
                        <Column className={styles.subtextColumn} align='right'>
                            {getPrimaryPhone() && <PhoneControl phone={getPrimaryPhone() as Phone} />}
                            {getFirstNotPrimaryPhone() && <PhoneControl phone={getFirstNotPrimaryPhone() as Phone} />}
                        </Column>
                    </Row>
                    {(!!contacts?.length || !!addContacts) && (
                        <>
                            <Row>
                                <Column>
                                    <CustomCardHeader
                                        title='Contacts'
                                        titleClassName={styles.contactsTitle}
                                        action={
                                            <>
                                                {!!addContacts && (
                                                    <SelectContactsDialog
                                                        title='Add Contact'
                                                        icon={<ContactCardRegular />}
                                                        party={party}
                                                        onSubmit={onContactSubmit}
                                                    >
                                                        <Button
                                                            className={addContactBtnClasses}
                                                            icon={<AddCircle12Regular />}
                                                            appearance='transparent'
                                                            data-testid='party-control-add-contact-button'
                                                        >
                                                            Add Contact
                                                        </Button>
                                                    </SelectContactsDialog>
                                                )}
                                            </>
                                        }
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <ContactPanel
                                        size='medium'
                                        contacts={contacts}
                                        onDelete={!!removeContacts ? onRemoveContact : undefined}
                                    />
                                </Column>
                            </Row>
                        </>
                    )}
                </BrandCard>
            )}

            {size === 'medium' && (
                <BrandCard
                    className={cardClasses}
                    maxWidth='350px'
                    minWidth='320px'
                    backgroundColor={appTokens.partyControl.background}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                    noGap
                    overflowX='clip'
                >
                    <Column overflowX='clip'>
                        <Persona
                            name={party?.name}
                            size='extra-large'
                            avatar={{ color: 'colorful', shape: 'square', image: { src: party?.logoUrl as string } }}
                            secondaryText={party?.address?.address1}
                            tertiaryText={cityStateZip() || ''}
                            quaternaryText={getPhoneText()}
                        />
                    </Column>
                </BrandCard>
            )}

            {size === 'small' && (
                <BrandCard
                    className={cardClasses}
                    maxWidth='350px'
                    minWidth='320px'
                    backgroundColor={appTokens.partyControl.background}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                    overflowX='clip'
                >
                    <Column overflowX='clip'>
                        <Persona
                            name={party?.name}
                            size='medium'
                            avatar={{ color: 'colorful', shape: 'square', image: { src: party?.logoUrl as string } }}
                            secondaryText={`${cityStateZip()}`}
                            tertiaryText={getPhoneText()}
                        />
                    </Column>
                </BrandCard>
            )}

            {size === 'option' && (
                <Persona
                    name={party?.name}
                    primaryText={<Text>{party?.name}</Text>}
                    size='small'
                    textAlignment='center'
                    avatar={{
                        color: 'colorful',
                        'aria-hidden': true,
                        shape: 'square',
                        image: { src: party?.logoUrl as string },
                    }}
                />
            )}

            {size === 'icon' && (
                <Tooltip content={party?.name || ''} relationship='label'>
                    <Persona
                        name={party?.name}
                        primaryText={''}
                        size='small'
                        textAlignment='center'
                        avatar={{
                            color: 'colorful',
                            'aria-hidden': true,
                            shape: 'square',
                            image: { src: party?.logoUrl as string },
                        }}
                    />
                </Tooltip>
            )}
        </>
    );
}

